/**
 * Les annonces de la régie.
 *
 * Le serveur les envoie déjà : `annoncer()` pousse un message `event` de type
 * `regie` à toute la salle, et le transport marche — c'est vérifiable dans le
 * journal. Ce qui manquait était de l'autre côté : le client se contentait de
 * remplacer la **phrase de Gom Gom** par le texte de l'annonce. Or cette
 * phrase n'est affichée que sur les écrans à qui on passe `gom`, c'est-à-dire
 * presque aucun depuis la refonte. L'annonce arrivait donc bien sur le second
 * appareil, et n'y était visible nulle part.
 *
 * Une annonce de régie, c'est quelqu'un qui parle à la salle, un vendredi
 * soir, parce qu'il se passe quelque chose. Depuis le 1er octobre, elle ne
 * s'ouvre plus en bandeau : Hoa la dit dans sa bulle, sur l'étape en cours
 * (hoa/messager.ts), puis reprend sa phrase. Un appui sur Hoa la rejoue.
 */
import { useEffect, useState } from "react";
import { abonnerMessager, annonceEnCours, dire, retirer } from "./hoa/messager.js";
import type { Expression } from "./hoa/Moai.js";
import { evenementCourant } from "./hoa/voix.js";

export type Annonce = { texte: string; a: number };

/**
 * La dernière annonce reçue du serveur, par sa date d'émission.
 *
 * L'application interroge `/api/annonce` toutes les vingt secondes : sans
 * mémoire, elle reposerait la même annonce sans fin, et celle qu'on vient de
 * fermer reviendrait aussitôt. La date d'émission suffit à les distinguer —
 * deux annonces différentes n'ont jamais la même.
 *
 * Elle est **écrite sur l'appareil**, et c'est le point.
 *
 * En mémoire seulement, elle repartait de zéro au moindre rechargement de
 * page : une application installée que le téléphone décharge et recharge en
 * revenant au premier plan, un retour à l'accueil depuis la régie qui change
 * l'adresse plutôt que le fragment, un simple F5. L'annonce fermée
 * réapparaissait alors sur l'écran suivant, puis sur le suivant, pendant
 * toute la demi-heure où elle reste vivante côté serveur. Sur le disque,
 * elle est fermée une fois pour toutes.
 */
const CLE_VUE = "annonce:vue";

function lireVue(): number {
  try {
    return Number(localStorage.getItem(CLE_VUE) ?? 0) || 0;
  } catch {
    return derniereVue;
  }
}

let derniereVue = 0;

function poserVue(a: number): void {
  derniereVue = a;
  try {
    localStorage.setItem(CLE_VUE, String(a));
  } catch {
    /* navigation privée : la mémoire vive fera l'affaire jusqu'au rechargement */
  }
}

/**
 * Une annonce venue du serveur — par le fil d'une salle ou par l'appel
 * régulier. Sans effet si c'est celle qu'on a déjà vue, fermée ou non.
 */
export function annonceDuServeur(a: number, texte: string): void {
  if (a <= Math.max(derniereVue, lireVue())) return;
  poserVue(a);
  poserAnnonce(texte);
}

export function poserAnnonce(texte: string, expression: Expression = "surpris"): void {
  // Dans un événement, c'est l'organisateur qui parle : son nom au-dessus.
  dire({ type: "annonce", texte, expression, auteur: evenementCourant()?.voix?.nom ?? null });
}

export function fermerAnnonce(): void {
  retirer("annonce");
}

export function useAnnonce(): Annonce | null {
  const lire = (): Annonce | null => {
    const m = annonceEnCours();
    return m ? { texte: m.texte, a: m.n } : null;
  };
  const [valeur, setValeur] = useState<Annonce | null>(lire);
  useEffect(() => abonnerMessager(() => setValeur(lire())), []);
  return valeur;
}

/**
 * À qui s'adresse l'annonce qu'on demande : le quartier où l'on est, ou
 * l'événement dans lequel on est entré. Une annonce de régie peut viser un
 * seul quartier, et une annonce d'événement ne concerne que ses invités.
 */
export function contexteAnnonce(): string {
  const p = new URLSearchParams();
  try {
    const evt = localStorage.getItem("hoa.evenement");
    if (evt) {
      const e = JSON.parse(evt) as { id?: string };
      if (e.id) p.set("evt", e.id);
    }
    const s = localStorage.getItem("hoa.secteur");
    if (s && !p.has("evt")) {
      const x = JSON.parse(s) as { id?: string };
      if (x.id) p.set("secteur", x.id);
    }
  } catch {
    /* rien de lisible : l'annonce de toute la ville */
  }
  return p.toString();
}
